import React, { createContext, useContext, useMemo } from 'react';
import { useAye } from '../hooks/useAye';
import { usePortalHook } from '../hooks/usePortalHook';
import { GuardedReturn } from '../utils/useGuardedCallback';

// Aye Types
import { DetectFaceResponse, RegisterCredentialsResponse } from '../core';

// Portal Types
import { DeleteAccountMethodRequestResponse } from '../core/api/http-methods/delete-account';
import { DeletePaymentMethodRequestResponse } from '../core/api/http-methods/delete-payment-method';
import { GetOtpResponse } from '../core/api/http-methods/get-otp';
import { GetOtpByEmailResponse } from '../core/api/http-methods/get-otp-by-email';
import { GetOtpByWhatsAppResponse } from '../core/api/http-methods/get-otp-by-whatsapp';
import { GetPaymentMethodsResponse } from '../core/api/http-methods/get-payment-methods';
import { GetUserTransactionsResponse } from '../core/api/http-methods/get-user-transactions';
import { LoginPortalResponse } from '../core/api/http-methods/login';
import { LoginByEmailResponse } from '../core/api/http-methods/login-by-email';
import { LoginTelegramResponse } from '../core/api/http-methods/login-telegram';
import { PayWithoutPINResponse } from '../core/api/http-methods/pay-without-pin';
import { ResetPINResponse } from '../core/api/http-methods/reset-pin';
import { SaveUserResponse } from '../core/api/http-methods/save-user-info';
import { SetDefaultResponse } from '../core/api/http-methods/set-default';
import { UserI } from '../types/user';

// create context
const AppContext = createContext<
  | {
      // Aye Context
      detectedFace: string | undefined;
      setDetectedFace: React.Dispatch<React.SetStateAction<string | undefined>>;
      face?: DetectFaceResponse;
      consent?: RegisterCredentialsResponse;
      // verifyConsent: (
      //   token: string,
      // ) => Promise<GuardedReturn<VerifyConsentResponse>>
      setFace: React.Dispatch<
        React.SetStateAction<DetectFaceResponse | undefined>
      >;
      setConsent: React.Dispatch<
        React.SetStateAction<RegisterCredentialsResponse | undefined>
      >;
      loading: boolean;
      selectedOptions: { option_1: number; option_2: number } | undefined;
      setSelectedOptions: React.Dispatch<
        React.SetStateAction<
          | {
              option_1: number;
              option_2: number;
            }
          | undefined
        >
      >;

      // Portal Context
      getCode: (
        phoneNumber: string,
        eventType: string
      ) => Promise<GuardedReturn<GetOtpResponse>>;
      getCodeByEmail: (
        email: string,
        eventType: string
      ) => Promise<GuardedReturn<GetOtpByEmailResponse>>;
      getCodeByWhatsApp: (
        phoneNumber: string,
        eventType: string
      ) => Promise<GuardedReturn<GetOtpByWhatsAppResponse>>;
      login: (
        phoneNumber: string,
        code: string
      ) => Promise<GuardedReturn<LoginPortalResponse>>;
      loginWithEmail: (
        email: string,
        otp: string
      ) => Promise<GuardedReturn<LoginByEmailResponse>>;
      loginWithTelegram: (idToken: string) => Promise<GuardedReturn<LoginTelegramResponse>>;
      token: string | undefined;
      user: UserI | undefined;
      logout: () => void;
      saveUser: (args_0: {
        user_name: string;
        user_email: string;
        user_birthday: string;
        newsletter: boolean;
        text_message: boolean;
        marketing_and_offers: boolean;
      }) => Promise<GuardedReturn<SaveUserResponse | undefined>>;
      // addPaymentMethod: (
      //   args_0: AddPaymentMethodRequest['body'],
      // ) => Promise<GuardedReturn<AddPaymentMethodResponse | undefined>>
      deletePaymentMethod: (args_0: {
        phoneNumber: string;
        consentId: string;
      }) => Promise<
        GuardedReturn<DeletePaymentMethodRequestResponse | undefined>
      >;
      transactions: (
        phoneNumber: string,
        page?: number,
        limit?: number
      ) => Promise<GetUserTransactionsResponse | undefined>;
      paymentMethods: () => Promise<
        GetPaymentMethodsResponse['payment_consents'] | undefined
      >;
      // verifyConsentPortal: (
      //   token: string,
      // ) => Promise<GuardedReturn<VerifyConsentResponse | undefined>>
      setDefault: (
        consentId: string
      ) => Promise<GuardedReturn<SetDefaultResponse | undefined>>;
      // updatePIN: (
      //   oldPin: string,
      //   newPin: string,
      //   transactionLimit: number,
      // ) => Promise<GuardedReturn<UpdatePINResponse | undefined>>
      // updateFace: (
      //   face_index: string,
      // ) => Promise<GuardedReturn<UpdateFaceResponse | undefined>>
      resetPIN: (
        newPin: string,
        otp: string,
        phoneNumber: string
      ) => Promise<GuardedReturn<ResetPINResponse | undefined>>;
      deleteAccount: (
        phoneNumber: string,
        otp: string
      ) => Promise<
        GuardedReturn<DeleteAccountMethodRequestResponse | undefined>
      >;
      payWithOutPin: (
        scanAndPayWithoutPin: boolean,
        transactionLimit: number,
        phoneNumber: string,
        pin: string | number
      ) => Promise<GuardedReturn<PayWithoutPINResponse | undefined>>;
      refreshUser: () => void;
    }
  | undefined
>(undefined);

const AyeProvider = ({ children }: any) => {
  const {
    detectedFace,
    setDetectedFace,
    face,
    consent,
    setFace,
    setConsent,
    loading: ayeLoading,
    selectedOptions,
    // verifyConsent,
    setSelectedOptions
  } = useAye();

  const {
    getCode,
    getCodeByEmail,
    getCodeByWhatsApp,
    login,
    loginWithEmail,
    loginWithTelegram,
    token,
    user,
    loading: portalLoading,
    logout,
    saveUser,
    // addPaymentMethod,
    deletePaymentMethod,
    transactions,
    paymentMethods,
    // verifyConsent: verifyConsentPortal,
    setDefault,
    // updatePIN,
    resetPIN,
    deleteAccount,
    payWithOutPin,
    refreshUser
    // updateFace,
  } = usePortalHook();

  const contextValue = useMemo(
    () => ({
      // Aye Values
      detectedFace,
      setDetectedFace,
      face,
      consent,
      setFace,
      setConsent,
      loading: ayeLoading || portalLoading, // combine both loading states
      selectedOptions,
      // verifyConsent,
      setSelectedOptions,

      // Portal Values
      payWithOutPin,
      deleteAccount,
      getCode,
      getCodeByEmail,
      getCodeByWhatsApp,
      login,
      loginWithEmail,
      loginWithTelegram,
      token,
      user,
      logout,
      saveUser,
      // addPaymentMethod,
      deletePaymentMethod,
      transactions,
      paymentMethods,
      // verifyConsentPortal,
      setDefault,
      // updatePIN,
      // updateFace,
      resetPIN,
      refreshUser
    }),
    [
      detectedFace,
      face,
      setDetectedFace,
      consent,
      setFace,
      setConsent,
      ayeLoading,
      portalLoading,
      selectedOptions,
      // verifyConsent,
      setSelectedOptions,
      getCode,
      getCodeByEmail,
      getCodeByWhatsApp,
      login,
      loginWithEmail,
      loginWithTelegram,
      token,
      user,
      logout,
      saveUser,
      // addPaymentMethod,
      deletePaymentMethod,
      transactions,
      paymentMethods,
      // verifyConsentPortal,
      setDefault,
      // updatePIN,
      resetPIN,
      payWithOutPin,
      deleteAccount,
      refreshUser
    ]
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

// context consumer hook
const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp was used outside of its Provider');
  }
  return context;
};

export { AyeProvider, useApp };
